<template>
  <k-modal id="add-new-evidence-modal" :show="showModal"
    @close="onClose"
    :closeOnClickAway="false"
  >
  <template #header>
    <h3>{{ modalTitle }}</h3>
  </template>
  <template #body>
    <div class="modal-body">
      <k-form :columns="10">
        <k-form-section :column-span="10">
          <k-form-field label="Evidence Name" :required="true" field-id="project-name-input">
            <input id="project-name-input" class="form-control" type="url" :readonly="editingDisabled" placeholder="Type Evidence name here..." v-model="evidenceName">
          </k-form-field>

          <k-form-field label="Evidence Description" :required="true" field-id="project-description-input">
            <textarea
              id="project-description-input"
              placeholder="Type description here..."
              v-model="evidenceDescription"
              rows="3"
              class="form-control"
              :maxlength="descMaxLength"
              :readonly="editingDisabled"
            />
            <k-character-count :value-length="evidenceDescription.length" :max-length="descMaxLength"></k-character-count>
          </k-form-field>
        </k-form-section>

        <k-form-section :column-span="10">
          <k-form-field label="Add Evidence" :required="true">
            <div class="evidence-upload-container">
              <div class="evidence-mode-toggle">
                <button
                  type="button"
                  class="btn btn-primary toggle-btn"
                  :class="{ active: isLinkMode }"
                  @click="setEvidenceMode('link')"
                  :disabled="editingDisabled"
                  aria-describedby="evidence-link-switch-description"
                >
                  Link
                </button>
                <p class="sr-only" id="evidence-link-switch-description">Use external link as evidence</p>
                <button
                  type="button"
                  class="btn btn-primary toggle-btn"
                  :class="{ active: !isLinkMode }"
                  @click="setEvidenceMode('file')"
                  :disabled="editingDisabled"
                  aria-describedby="evidence-file-switch-description"
                >
                  Upload
                </button>
                <p class="sr-only" id="evidence-file-switch-description">Upload file as evidence</p>
              </div>
              <div v-if="isLinkMode" class="evidence-link-wrapper">
                <p for="project-evidence-input">You can also upload a file. Switch using the above buttons.</p>
                <span class="helper-text">Please save your evidence link before submitting the form</span>
                <div v-if="!isEditingUrl" class="evidence-link-view">
                  <div class="evidence-link-container">
                    <a :href="evidence.url" target="_blank">{{ evidence.url }}</a>
                  </div>
                  <button class="btn btn-outlined" @click="isEditingUrl = true" :disabled="editingDisabled" title="Edit evidence resource link" aria-label="Edit evidence resource link">
                  <i class="fas fa-pencil-alt" aria-hidden="true"></i>
                  </button>
                </div>
                <div v-else class="evidence-link-edit">
                  <input class="form-control" type="url" v-model="evidence.url" :readonly="editingDisabled" placeholder="Add evidence resource link here..." />
                  <button class="btn btn-success" @click="setUrlEvidence(evidence.url)" :disabled="!evidence.url.trim()" title="Save evidence resource link" aria-label="Save evidence resource link">
                    <i class="fas fa-check" aria-hidden="true"></i>
                  </button>
                </div>
                <span v-if="!isUrlValid" class="helper-text">{{ urlValidationMessage }}</span>
              </div>
              <div v-else class="evidence-upload-wrapper">
                <p for="project-evidence-input">You can also submit an external link as an evidence. Switch using the above buttons.</p>
                <span v-if="!evidence.file && evidenceData?.signed_url && modalMode !== 'create'" class="upload-replacement-text">
                  Upload a new file to replace the current one
                </span>
                <dropfield
                  @file="setFileEvidence($event[0])"
                  id="upload-evidence-document"
                  :immediatePost="false"
                  :enablePreviews="true"
                  ref="evidenceUploadField"
                  :disableDropfield="editingDisabled"
                  :customMessage="dropfieldMessage"
                  :accept="acceptedFileTypes"
                  :acceptedFilesInfo="acceptedFilesInfo"
                  />
              </div>
            </div>
          </k-form-field>
        </k-form-section>

        <k-form-section :column-span="10" legend="Competencies" :required="true">
          <small>Select Competencies relevant to the evidence</small>
            <template v-if="projectCompetenciesReady && projectCompetencies.length > 0">
              <k-editable-table
                v-model="projectCompetencies"
                :headers="tableHeaders"
                :max="10"
                :initial-sort-state="initialSortState"
              />
            </template>
            <div v-else>
              <span class="label-text">No competencies found for this project</span>
            </div>
        </k-form-section>
      </k-form>
    </div>
  </template>
  <template #footer>
    <div class="modal-footer-left">
      <button class="btn btn-outlined" @click="onClose">Cancel</button>
    </div>
    <div class="modal-footer-right">
      <button v-if="modalMode === 'edit'" class="btn btn-danger delete-draft" @click="confirmDeleteEvidence" :disabled="cannotDeleteEvidence || editingDisabled">Delete Evidence</button>
      <button class="btn btn-success submit" @click="saveNewEvidence" :disabled="!validSubmitForReview || editingDisabled">Submit for Review</button>
    </div>
  </template>
  </k-modal>
</template>

<style>
#add-new-evidence-modal .modal-footer {
  display: flex;
  justify-content: space-between;
}

#add-new-evidence-modal .modal-container {
  width: 80vw;
}

#add-new-evidence-modal .form-control[readonly],
#add-new-evidence-modal textarea[readonly] {
  background-color: var(--input-background);
  border: var(--input-border);
  cursor: not-allowed;
}
</style>

<style scoped>
.modal-body {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.evidence-upload-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.evidence-mode-toggle {
  display: flex;
  gap: 0;
  margin: 15px 0;
}

.evidence-mode-toggle .toggle-btn {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-right: 1px solid var(--input-border);
  margin: 0;
  min-width: 120px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
  background-color: var(--kate-button-primary);
  color: var(--kate-type-dark);
  border-color: var(--kate-button-primary);
}

.evidence-mode-toggle .toggle-btn:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}

.evidence-mode-toggle .toggle-btn:not(:last-child) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.evidence-mode-toggle .toggle-btn:last-child {
  border-right: none;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

.evidence-mode-toggle .toggle-btn:hover:not(:disabled):not(.active) {
  background-color: var(--kate-button-primary-hover);
  color: var(--kate-type-light);
}

.evidence-mode-toggle .toggle-btn.active {
  background-color: var(--kate-background);
  border-color: var(--kate-background);
  color: var(--kate-type-white);
}

.evidence-mode-toggle .toggle-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.evidence-link-wrapper,
.evidence-upload-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 10px;
}

.evidence-link-wrapper > label,
.evidence-upload-wrapper > label {
  font-weight: 600;
  color: var(--kate-type-primary);
  margin-bottom: 5px;
}

.evidence-upload-container > span {
  align-self: center;
  font-weight: bold;
  margin-top: 20px;
}

.evidence-link-view,
.evidence-link-edit {
  display: flex;
  width: 100%;
  gap: 10px;
  align-items: center;
}

.evidence-link-container {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--input-border);
  border-radius: 4px;
  background-color: var(--input-background);
  font-family: inherit;
  line-height: 1.5;
  min-height: 38px;
  display: flex;
  align-items: center;
}

.evidence-upload-container :deep(.k-dropbox) {
  display: flex;
  min-height: 150px;
  height: auto;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.evidence-upload-container :deep(.k-dropbox .upload-text) {
  font-weight: 500;
  color: var(--kate-type-secondary);
}

.evidence-upload-container :deep(.k-dropbox i) {
  margin-top: 12px;
  font-size: 20px;
  color: var(--kate-type-primary);
}

#upload-evidence-document:hover {
  border-color: var(--kate-button-primary);
}

.evidence-upload-container :deep(.k-dropbox) > label {
  display: block;
  padding: 0;
  font-size: 1em;
}

.helper-text {
  font-size: 0.9em;
  color: var(--kate-type-secondary);
  margin: 0;
  font-style: italic;
}

.upload-replacement-text {
  font-size: 0.9em;
  color: var(--kate-type-secondary);
  font-style: italic;
}
</style>

<script>
import KModal from '@base-components/k-modal.vue';
import {
  KForm, KFormField, KFormSection, KCharacterCount,
} from '@base-components/forms';
import KEditableTable from '@base-components/k-editable-table.vue';
import Dropfield from '@base-components/dropfield.vue';
import { competencySortBy } from '@utils/sort-table-competencies';
import { ACCEPTED_FILE_TYPES, ACCCEPTED_FILE_EXTENSIONS } from './constants';

export default {
  components: {
    KModal,
    KEditableTable,
    Dropfield,
    KForm,
    KFormField,
    KFormSection,
    KCharacterCount,
  },

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    projectId: {
      type: Number,
      required: true,
    },
    programmeId: {
      type: Number,
      required: true,
    },
    modalMode: {
      type: [String, undefined],
    },
    evidenceData: {
      type: Object,
      default: undefined,
    },
  },

  data() {
    return {
      descMaxLength: 2048,
      isUploading: false,
      isUpdating: false,
      evidenceName: '',
      evidenceDescription: '',
      projectCompetencies: [],
      projectCompetenciesReady: false,
      evidenceId: null,

      evidence: {
        type: 'file', // by default link otherwise some kind of type
        file: null, // File object or null
        url: '', // URL string
        path: null, // uploaded file path
      },
      isUrlValid: false,
      urlValidationMessage: '',
      isEditingUrl: true,
    };
  },

  watch: {
    showModal(newVal) {
      if (newVal) {
        this.getProjectCompetencies();
      }
    },

    isUpdating(newVal) {
      if (newVal) {
        this.$Loading.minimal();
      } else {
        this.$Loading.finish();
      }
    },

    'evidence.url': {
      handler(newVal) {
        if (newVal.trim()) {
          this.isUrlValid = this.validateUrl(newVal);
        } else {
          this.isUrlValid = false;
          this.urlValidationMessage = '';
        }
      },
      immediate: true,
    },

    evidenceData: {
      handler(newVal) {
        if (newVal) {
          this.evidenceName = newVal.name || '';
          this.evidenceDescription = newVal.description || '';
          this.evidenceId = newVal.evidenceId;

          if (newVal.signed_url && newVal.signed_url.trim() !== '') {
            // File evidence (for all the file types)
            this.evidence = {
              type: 'file',
              file: null,
              url: '',
              path: newVal.evidence_link,
            };
            this.isEditingUrl = false;
          } else {
            // URL evidence (only links)
            this.evidence = {
              type: 'link',
              file: null,
              url: newVal.evidence_link || '',
              path: null,
            };
            this.isEditingUrl = false;
          }
        } else {
          this.clearFields();
        }
      },
      immediate: true,
    },
  },

  computed: {
    acceptedFileTypes() {
      return ACCEPTED_FILE_TYPES.join(',');
    },

    acceptedFileExtensions() {
      return ACCCEPTED_FILE_EXTENSIONS.join(', ');
    },

    evidenceInputMode() {
      return this.evidence.type;
    },

    isLinkMode() {
      return this.evidenceInputMode === 'link';
    },

    evidenceType() {
      // allowed types ["document", "link", "file", "video", "image", "pdf"],
      const documentKeywords = ['document', 'word', 'excel', 'powerpoint'];
      if (this.evidence.file) {
        const mimeType = this.evidence.file.type;
        if (mimeType.startsWith('image/')) return 'image';
        if (mimeType.startsWith('video/')) return 'video';
        if (mimeType === 'application/pdf') return 'pdf';
        if (documentKeywords.some(document => mimeType.includes(document))) return 'document';
        return 'file';
      }
      return 'link';
    },

    evidenceLink() {
      return this.evidence.file ? this.evidence.path : this.evidence.url;
    },

    hasValidEvidence() {
      return Boolean(this.evidence.file) || this.isUrlValid;
    },

    modalTitle() {
      if (this.modalMode === 'view' && this.evidenceData) {
        return `View ${this.evidenceData.name}`;
      }
      return this.modalMode === 'edit' && this.evidenceData ? `Edit ${this.evidenceData.name}` : 'Add New Evidence';
    },

    validName() {
      return this.evidenceName.trim() !== '';
    },

    validDescription() {
      return this.evidenceDescription.trim() !== '';
    },

    validCompetencies() {
      return this.listSelectedCompetencies().length > 0;
    },

    validSubmitForReview() {
      return Boolean(this.validName && this.validDescription && this.validCompetencies && this.hasValidEvidence && !this.isEditingUrl);
    },

    editingDisabled() {
      return this.modalMode === 'view' || this.evidenceData?.approved || this.isUpdating;
    },

    cannotDeleteEvidence() {
      return this.evidenceData?.approved === true;
    },

    dropfieldMessage() {
      return `<i class="fas fa-cloud-upload-alt" aria-hidden="true"></i>
      <span class="upload-text">Click or drag evidence file here to upload</span>`;
    },

    acceptedFilesInfo() {
      return `<p>Accepted File Types: ${this.acceptedFileExtensions}</p>`;
    },

    initialSortState() {
      return { headerKey: 'label', ascending: true };
    },

    tableHeaders() {
      return [
        {
          key: 'checkbox',
          title: '',
          editable: !this.editingDisabled,
          type: 'boolean',
          required: true,
          sortable: false,
        },
        {
          key: 'label',
          title: 'Label',
          editable: false,
          sortable: true,
          filterable: true,
          type: 'string',
          sortBy: competencySortBy,
        },
        {
          key: 'category',
          title: 'Category',
          editable: false,
          sortable: true,
          filterable: true,
          type: 'string',
        },
        {
          key: 'description',
          title: 'Description',
          editable: false,
          sortable: true,
          filterable: true,
          type: 'string',
        },
      ];
    },
  },

  methods: {
    resetFileField() {
      if (this.$refs.evidenceUploadField) {
        this.$refs.evidenceUploadField.reset();
      }
    },

    setFileEvidence(file) {
      this.evidence = {
        type: 'file',
        file,
        url: '',
        path: null,
      };
      this.isEditingUrl = false;
    },

    setUrlEvidence(url) {
      this.evidence = {
        type: 'link',
        file: null,
        url,
        path: null,
      };
      this.isEditingUrl = false;
      this.resetFileField();
    },

    clearEvidence() {
      this.evidence = {
        type: this.evidence.type,
        file: null,
        url: '',
        path: null,
      };
      this.isEditingUrl = true;
      this.resetFileField();
    },

    getProjectCompetencies() {
      this.projectCompetenciesReady = false;
      this.$Loading.start();
      this.$logger.info('Fetching all competencies for the project', this.projectId);
      this.$http.get(`/api/curriculum/programmes/projects/${this.projectId}/competencies`)
        .then(response => {
          this.$logger.info('Succefully fetched all competencies for the project', this.projectId);
          this.projectCompetencies = response.data || [];
          // if in view mode / edit mode
          this.markSelectedCompetencies();
        })
        .catch(err => {
          this.$logger.autowarn('Error fetching competencies for project', this.projectId, err);
          this.$ktoast.warning('Error fetching competencies');
          this.projectCompetencies = [];
        }).then(() => {
          this.projectCompetenciesReady = true;
          this.$Loading.finish();
        });
    },

    listSelectedCompetencies() {
      return this.projectCompetencies.filter(competency => competency.checkbox);
    },

    async saveNewEvidence() {
      try {
        this.isUpdating = true;
        let evidenceId;
        if (this.modalMode === 'create') {
          const evidencePayload = {
            programme_id: this.programmeId,
            name: this.evidenceName,
            description: this.evidenceDescription,
            project_competency_mapping_ids: this.listSelectedCompetencies().map(competency => competency.id),
          };
          this.$logger.info('Creating project evidence entry');
          const response = await this.$http.post(`/api/curriculum/programmes/projects/${this.projectId}/evidence`, evidencePayload);
          this.$ktoast.success('Project evidence entry created successfully', response.data);
          evidenceId = response.data.project_evidence_id;
        } else {
          evidenceId = this.evidenceId;
          this.$logger.info('Updating existing project evidence', { evidenceId });
        }

        // check if it's file
        if (this.evidence.file) {
          await this.uploadEvidenceFile(evidenceId);
        }
        await this.createEvidenceVersion(evidenceId);
        this.$logger.info('Project evidence version created successfully');
        this.$ktoast.success('Project evidence submitted for review');
        this.$emit('close');
        this.$emit('update');
        this.clearFields();
      } catch (err) {
        this.$logger.autowarn('Error saving project evidence', undefined, err);
        this.$ktoast.warning('Error submitting project evidence for review');
      } finally {
        this.isUpdating = false;
      }
    },

    async createEvidenceVersion(evidenceId) {
      const versionPayload = {
        programme_id: this.programmeId,
        name: this.evidenceName,
        description: this.evidenceDescription,
        evidence_type: this.evidenceType,
        evidence_link: this.evidenceLink,
        feedback_text: null,
        project_competency_mapping_ids: this.listSelectedCompetencies().map(competency => competency.id),
      };
      this.$logger.info('Creating project evidence version', { evidenceId, payload: versionPayload });
      await this.$http.post(`/api/curriculum/programmes/projects/${this.projectId}/evidence/${evidenceId}/versions`, versionPayload);
    },

    async uploadEvidenceFile(evidenceId) {
      if (!this.evidence.file) return;
      try {
        this.isUploading = true;
        const uploadUrlPayload = {
          file_name: this.evidence.file.name,
          file_size: this.evidence.file.size,
          content_type: this.evidence.file.type,
        };
        this.$logger.info('Getting upload URL for evidence file');
        const uploadResponse = await this.$http.post(
          `/api/curriculum/programmes/projects/${this.projectId}/evidence/${evidenceId}/upload-url?programme_id=${this.programmeId}`,
          uploadUrlPayload,
        );

        const { upload_url: uploadUrl, file_path: filepPath } = uploadResponse.data;
        this.evidence.path = filepPath;

        this.$logger.info('Uploading file to signed URL');
        await this.$http.put(uploadUrl, this.evidence.file);

        this.$logger.info('File uploaded successfully', { filePath: filepPath });
      } catch (err) {
        this.$logger.autowarn('Error uploading evidence file', undefined, err);
        throw err;
      } finally {
        this.isUploading = false;
      }
    },

    confirmDeleteEvidence() {
      this.$ktoast.confirmToast(`Are you sure you want to delete this evidence: <strong>${this.evidenceData.name}</strong>?`, 'warning', () => {
        this.deleteEvidence();
      });
    },

    deleteEvidence() {
      this.isUpdating = true;
      this.$logger.info('Deleting project evidence', {
        projectId: this.projectId,
        evidenceId: this.evidenceId,
      });
      this.$http.delete(`/api/curriculum/programmes/projects/${this.projectId}/evidence/${this.evidenceId}`, {
        data: {
          programme_id: this.programmeId,
        },
      })
        .then(() => {
          this.$ktoast.success('Project evidence deleted');
          this.$logger.info('Project evidence deleted', {
            projectId: this.projectId,
            evidenceId: this.evidenceId,
          });
          this.$emit('close');
          this.$emit('update');
          this.clearFields();
        }).catch(err => {
          this.$logger.autowarn('Error deleting project evidence', {
            projectId: this.projectId,
            evidenceId: this.evidenceId,
          }, err);
          this.$ktoast.warning('Error deleting project evidence');
        }).finally(() => {
          this.isUpdating = false;
        });
    },

    clearFields() {
      this.evidenceName = '';
      this.evidenceDescription = '';
      this.projectCompetencies = [];
      this.evidenceId = null;
      this.clearEvidence();
    },

    onClose() {
      this.clearFields();
      this.$emit('close');
    },

    markSelectedCompetencies() {
      if (!this.evidenceData || !this.evidenceData.competency_mappings) return;
      const selectedCompetencyIds = this.evidenceData.competency_mappings.map(mapping => mapping.project_competency_mapping_id);
      this.projectCompetencies.forEach((competency, index) => {
        this.projectCompetencies[index].checkbox = selectedCompetencyIds.includes(competency.id);
      });
    },

    setEvidenceMode(mode) {
      this.clearEvidence();
      this.evidence.type = mode;
    },

    validateUrl(url) {
      const { isValid, message } = this.$validator.urlValidator(url);
      this.urlValidationMessage = message;
      return isValid;
    },
  },
};
</script>
